import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Clock, Download } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { useSite } from "../../context/SiteContext";
import { PolicyLinksNote } from "../site/LegalLinks";
import { BilledAs } from "../payments/CardMarks";
import { MODE_LABELS, formatEventDate, humanize, money, venueLine } from "../../lib/format";

const Row = ({ label, value, testid }) => (
  <div className="flex justify-between gap-4 border-b border-slate-200 py-2"><dt className="text-[#4A5568]">{label}</dt><dd className="text-right font-semibold text-navy" data-testid={testid}>{value}</dd></div>
);

export default function StepConfirmation({ result, onReset }) {
  const [reg, setReg] = useState(result.registration);
  const [dl, setDl] = useState(false);
  const { legal, settings } = useSite();
  useEffect(() => {
    api.get(`/public/registrations/${result.registration.reference}`, { params: { token: result.token } }).then((r) => setReg(r.data)).catch(() => {});
  }, [result]);
  const ticket = async () => {
    setDl(true);
    try {
      const r = await api.get(`/public/registrations/${reg.reference}/ticket.pdf`, { params: { token: result.token }, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = Object.assign(document.createElement("a"), { href: url, download: `ticket-${reg.reference}.pdf` });
      document.body.appendChild(a); a.click(); a.remove(); setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) { toast.error("The ticket could not be downloaded. Please try again."); } finally { setDl(false); }
  };
  const ev = reg.event;
  const confirmed = reg.registration_status === "confirmed";
  return (
    <section data-testid="step-confirmation" aria-labelledby="conf-h">
      <div className="text-center">
        {confirmed ? <CheckCircle2 className="mx-auto text-green-700" size={56} aria-hidden="true" /> : <Clock className="mx-auto text-gold" size={56} aria-hidden="true" />}
        <h2 id="conf-h" className="mt-3 text-3xl text-navy">{confirmed ? "Registration Confirmed" : "Registration Received"}</h2>
        {reg.is_test && <p className="mt-2 inline-block rounded bg-gold px-3 py-1 text-xs font-bold text-navy" data-testid="confirmation-test-flag">TEST REGISTRATION</p>}
        {reg.is_verification && <p className="mt-2 inline-block rounded bg-burgundy px-3 py-1 text-xs font-bold text-white" data-testid="confirmation-verification-flag">VERIFICATION – BSD 1.00</p>}
        <p className="mt-4 text-[#4A5568]">Your reference number</p>
        <p className="font-serif text-3xl font-bold tracking-wide text-burgundy" data-testid="confirmation-reference">{reg.reference}</p>
      </div>
      <dl className="mx-auto mt-8 max-w-xl">
        <Row label="Attendee" value={`${reg.title} ${reg.first_name} ${reg.last_name}`} testid="confirmation-name" />
        <Row label="Category" value={reg.category_name} />
        <Row label="Attendance mode" value={MODE_LABELS[reg.attendance_mode]} testid="confirmation-attendance-mode" />
        <Row label="Event" value={ev.name} />
        <Row label="Date" value={`${formatEventDate(ev)}${ev.date_confirmed ? "" : " (to be confirmed)"}`} testid="confirmation-event-date" />
        <Row label="Location" value={venueLine(ev)} />
        <Row label="Amount" value={money(reg.quote.total, reg.quote.currency)} testid="confirmation-amount" />
        <Row label="Registration status" value={humanize(reg.registration_status)} testid="confirmation-registration-status" />
        <Row label="Payment status" value={humanize(reg.payment_status)} testid="confirmation-payment-status" />
        <Row label="Confirmation email" value={reg.email_status === "logged" ? "Logged (not sent: email delivery not configured)" : humanize(reg.email_status)} testid="confirmation-email-status" />
      </dl>
      {reg.ticket_available && (
        <div className="mt-6 text-center">
          <button type="button" className="btn-primary" onClick={ticket} disabled={dl} data-testid="confirmation-ticket-download-btn">
            <Download size={18} aria-hidden="true" className="mr-2 inline" />{dl ? "Preparing…" : "Download your ticket (PDF)"}</button>
        </div>)}
      {reg.attendance_mode === "virtual" && <p className="mt-6 text-center text-sm text-[#4A5568]">Virtual delegates join on the <Link to="/virtual" className="font-semibold text-burgundy underline" data-testid="confirmation-virtual-link">Join Virtual Event</Link> page using this email and reference.</p>}
      {reg.quote?.total > 0 && reg.payment_status === "completed" && (
        <BilledAs descriptor={settings?.billing_descriptor} className="mt-6 text-center" testid="confirmation-billing-descriptor" />)}
      <PolicyLinksNote pages={legal?.pages} className="mt-6 text-center" />
      <div className="mt-8 text-center"><button type="button" className="btn-outline" onClick={onReset} data-testid="register-another-btn">Register another attendee</button></div>
    </section>
  );
}
